import React, { useState, useCallback, memo, ReactNode } from "react"
import {
  Platform,
  TextInputProps,
  NativeSyntheticEvent,
  TextInputContentSizeChangeEventData,
  StyleProp,
  TextStyle,
} from "~/ui/primitives"
import styled from "~/ui/styled"
import { flattenStyle } from "~/ui/style"

interface MultiLineTextFieldProps extends TextInputProps {
  label?: string | ReactNode;
  value?: string
  required?: boolean;
  onChangeText?: (text: string) => void
  maxLength?: number
  numberOfLines?: number
  autoHeight?: boolean // 是否根据内容自动增高
  minHeight?: number
  maxHeight?: number
  error?: string | false
  style?: StyleProp<TextStyle>
}

const MultiLineTextField: React.FC<MultiLineTextFieldProps> = ({
  label = "",
  value = "",
  required = false,
  onChangeText,
  maxLength,
  numberOfLines = 4,
  autoHeight: autoHeightProp,
  minHeight = 80,
  maxHeight = 200,
  error = "",
  style,
  ...rest
}) => {
  const containerStyle = flattenStyle(style)
  const hasFixedHeight = containerStyle.height != null
  const autoHeight = autoHeightProp ?? !hasFixedHeight

  const [inputHeight, setInputHeight] = useState(minHeight)

  /** 自动增长高度 */
  const onContentSizeChange = useCallback(
    (e: NativeSyntheticEvent<TextInputContentSizeChangeEventData>) => {
      if (!autoHeight) return
      let newHeight = e.nativeEvent.contentSize.height
      if (newHeight < minHeight) newHeight = minHeight
      if (newHeight > maxHeight) newHeight = maxHeight
      setInputHeight(newHeight)
    },
    [autoHeight, minHeight, maxHeight],
  )

  /** 文本变化 + 最大字数限制 */
  const handleTextChange = useCallback(
    (text: string) => {
      if (maxLength && text.length > maxLength) {
        text = text.slice(0, maxLength)
      }
      onChangeText?.(text)
    },
    [maxLength, onChangeText],
  )

  return (
    <FormGroup>
      {label && (
        <LabelWrapper>
          {typeof label === "string" ? <LabelText>{required && <RequiredText>*&nbsp;</RequiredText>}{label}</LabelText> : label}
        </LabelWrapper>
      )}
      <Container
        error={!!error}
        style={[
          {
            minHeight,
            height: autoHeight ? inputHeight : containerStyle.height ?? minHeight,
          },
          style,
        ]}
      >
        <StyledInput
          multiline
          numberOfLines={numberOfLines}
          textAlignVertical="top"
          value={value}
          underlineColorAndroid="transparent"
          onChangeText={handleTextChange}
          onContentSizeChange={onContentSizeChange}
          {...rest}
          style={{
            flex: 1,
            minHeight: 0,
            height: "100%",
          }}
        />

        {maxLength && (
          <LengthIndicator>
            {value?.length}/{maxLength}
          </LengthIndicator>
        )}
      </Container>

      {error ? <ErrorText>{error}</ErrorText> : null}
    </FormGroup>
  )
}

const FormGroup = styled.View`
  flex-direction: column;
  width: 100%;
  min-width: 0;
  max-width: 100%;
  box-sizing: border-box;
`;

const Container = styled.View<{ error: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;
  max-width: 100%;
  box-sizing: border-box;
  padding: 10px;
  border-radius: 8px;
  overflow: hidden;
  ${({ theme, error }) =>
    Platform.OS === "web"
      ? `
    border: 1.5px solid ${error ? theme.colors.error : theme.colors.border};
    background-color: ${theme.colors.blockBackground};
  `
      : `
    border-width: 1px;
    border-style: solid;
    border-color: ${error ? theme.colors.error : theme.colors.border};
  `}
`

const LabelWrapper = styled.View`
  flex-direction: row;
  align-items: center;
  padding: 12px 0 8px 0;
`;

const LabelText = styled.Text`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text};
  font-weight: 600;
`;

const RequiredText = styled.Text`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.error};
  font-weight: 400;
`;

const StyledInput = styled.TextInput`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text};
  flex: 1 1 auto;
  width: 100%;
  min-width: 0;
  max-width: 100%;
  box-sizing: border-box;
  ${Platform.OS === "web"
    ? `
    display: block;
    outline: none;
    border: none;
    background: transparent;
    resize: none;
    line-height: 1.45;
    white-space: pre-wrap;
    word-break: break-word;
  `
    : ""}
`

const LengthIndicator = styled.Text`
  position: absolute;
  bottom: 6px;
  right: 10px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.secondary};
`

const ErrorText = styled.Text`
  margin-top: 4px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.error};
`

export default memo(MultiLineTextField)
